import { useAxiosInstance } from '@gov-nx/api/axios';
import { usePoQuery } from '@gov-nx/api/hooks/usePoQuery';
import {
	fetchDataBoxesMessageQuery,
	searchDataBoxesQuery,
} from '@gov-nx/api/queries/dataBoxQuery';
import { propEq } from '@gov-nx/core/types/helpers.types';
import { useDataBoxStore } from '@gov-nx/store/data-box/store';
import type { DataBoxFolderType } from '../providers/DataBoxes.types';

export const useMessageDetail = ({
	dataBoxId,
	folderType,
	messageId,
}: {
	dataBoxId?: string;
	folderType?: DataBoxFolderType;
	messageId?: number;
}) => {
	const dataBoxes = useDataBoxStore('list');
	const isMessageQueryEnabled = !!dataBoxId && !!messageId && !!folderType;
	const { axiosInstance } = useAxiosInstance();

	const messageQuery = usePoQuery({
		queryKey: ['data-box-message', dataBoxId, messageId, folderType],
		queryFn: () => {
			if (!dataBoxId || !messageId || !folderType) {
				return undefined;
			}

			return fetchDataBoxesMessageQuery({
				axiosInstance,
				dataBoxId,
				folderType,
				messageId,
			});
		},
		retry: 0,
		refetchOnWindowFocus: false,
		enabled: isMessageQueryEnabled,
	});

	const message = messageQuery.data;

	const oppositeDataBoxQuery = usePoQuery({
		queryKey: ['data-box-message', message],
		queryFn: async () => {
			const oppositeDataBoxId =
				dataBoxId === message?.prijemceId
					? message?.odesilatelId
					: message?.prijemceId;

			return searchDataBoxesQuery(
				axiosInstance,
				oppositeDataBoxId as string,
				'GENERAL'
			);
		},
		enabled: !!message,
		retry: 0,
		refetchOnWindowFocus: false,
	});

	const currentDataBox = dataBoxes.find(propEq('datovaSchrankaId', dataBoxId));
	const oppositeDataBox = oppositeDataBoxQuery.data?.seznam?.[0];

	const isCurrentDataboxRecipient = dataBoxId === message?.prijemceId;
	const recipientDataBox = isCurrentDataboxRecipient
		? currentDataBox
		: oppositeDataBox;
	const isCurrentDataboxSender = dataBoxId === message?.odesilatelId;
	const senderDataBox = isCurrentDataboxSender
		? currentDataBox
		: oppositeDataBox;

	return {
		message,
		isMessageLoading: messageQuery.isInitialLoading,
		refetchMessage: messageQuery.refetch,
		isDataBoxLoading: oppositeDataBoxQuery.isInitialLoading,
		currentDataBox,
		recipientDataBox,
		senderDataBox,
	};
};
